/** Summarize search filters and detect broad/unfiltered searches. */

export function isBroadSearch(
  filters: Record<string, unknown> | null | undefined,
  searchName?: string | null,
): boolean {
  if (searchName?.trim().toLowerCase() === 'default') return true;
  const f = filters || {};
  const restrictiveKeys = [
    'search_text',
    'searchText',
    'keyword',
    'brand',
    'model',
    'trim',
    'body_type',
    'year_min',
    'year_max',
    'price_min',
    'price_max',
    'city',
    'region',
    'location',
    'rooms_min',
    'rooms_max',
    'sqm_min',
    'sqm_max',
    'area_min',
    'area_max',
    'condition',
    'color',
  ];
  return !restrictiveKeys.some((key) => {
    const v = f[key];
    if (v == null || v === '') return false;
    if (typeof v === 'number') return true;
    if (typeof v === 'string') return v.trim().length > 0;
    if (typeof v === 'boolean') return v;
    return false;
  });
}

function rangeLabel(prefix: string, min: unknown, max: unknown): string | null {
  if (min == null && max == null) return null;
  return `${prefix} ${min ?? '…'}–${max ?? '…'}`;
}

export function formatFiltersSummary(
  filters: Record<string, unknown> | null | undefined,
  searchName?: string | null,
): string {
  if (isBroadSearch(filters, searchName)) {
    return 'No filters — may match all vehicle listings';
  }
  const f = filters || {};
  const parts: string[] = [];
  const text = f.search_text ?? f.searchText ?? f.keyword;
  if (text) parts.push(`"${String(text)}"`);
  if (f.brand) parts.push(`Make: ${f.brand}`);
  if (f.model) parts.push(`Model: ${f.model}`);
  if (f.trim) parts.push(`Trim: ${f.trim}`);
  if (f.body_type) parts.push(`Body: ${f.body_type}`);
  const year = rangeLabel('Year', f.year_min, f.year_max);
  if (year) parts.push(year);
  const price = rangeLabel('Price', f.price_min, f.price_max);
  if (price) parts.push(price);
  if (f.city) parts.push(`City: ${f.city}`);
  if (f.region) parts.push(`Region: ${f.region}`);
  if (f.location) parts.push(`Location: ${f.location}`);
  if (f.remote_only) parts.push('Remote only');
  if (f.salary_min) parts.push(`Salary min: ${f.salary_min}`);
  if (f.condition) parts.push(`Condition: ${f.condition}`);
  const rooms = rangeLabel('Rooms', f.rooms_min, f.rooms_max);
  if (rooms) parts.push(rooms);
  const sqm = rangeLabel('m²', f.sqm_min ?? f.area_min, f.sqm_max ?? f.area_max);
  if (sqm) parts.push(sqm);
  if (f.property_type) parts.push(String(f.property_type));
  return parts.length ? parts.join(' · ') : 'No filters';
}
